import {
  Button,
  Card,
  Col,
  Flex,
  Row,
  Space,
  Table,
  Tag,
  Spin,
  Typography,
  Modal,
  Upload,
  Image,
} from "antd";
import React, { useState } from "react";
import Title from "antd/es/typography/Title";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useDebounce from "../../../../../../hooks/useDebounce";
import { appConstConfig } from "../../../../../../constants/config/appConstConfig";
import { getServicesAction } from "../../../../../../redux/services/services/servicesServices";
import Actions from "../../../../../../redux/actions";
import { routes, userRoles } from "../../../../../../constants";
import { baseRouteMaker } from "../../../../../../constants/routeConsts";
import Spinner from "../../../../../common/spinner/Spinner";
import { colors } from "../../../../../../constants/colors";
import { DeleteIcon, EditIcon } from "../../../../../../assets/svg";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { getServiceProviderAppointmentsInfoByIdAction } from "../../../../../../redux/services/service-provider-appointments/serviceProviderAppointmentsServices";
import { GetCardLayout } from "../../../../../common/getCardLayout";
import RequiredLabel from "../../../../../common/required-label/RequiredLabel";
import ServiceInfoFormDialog from "./ServiceInfoFormDialog";
import ServiceInfoDeleteDialog from "./ServiceInfoDeleteDialog";
import {
  dateObjToDispViewDate,
  downloadUrl,
  formulateAttachmentPreview,
  readImageFileAsync,
  timeObjToDispViewTimeWithSuffix,
} from "../../../../../../utils/utils";
import useLocalStorage from "../../../../../../hooks/useLocalStorage";
import { appConfig } from "../../../../../../constants/config/appConfig";
import { appAssetsConfig } from "../../../../../../constants/config/appAssetsConfig";
import dayjs from "dayjs";
import { StatusTag } from "../../../../../common/status-tag";

const ServiceInfoList = () => {
  /* i18n dependencies */
  const { t } = useTranslation();
  /* i18n dependencies */

  /* dispatch an action */
  const dispatch = useDispatch();
  /* dispatch an action */

  /* For navigating between pages */
  const navigate = useNavigate();
  /* For navigating between pages */

  /* loading dependencies */
  const [loading, setLoading] = useState(false);
  /* loading dependencies */

  const { userSession } = useLocalStorage(appConfig.SESSION_USER_INFO);

  /* detail dependencies */
  const [appointmentsInfoDetails, setAppointmentsInfoDetails] = useState(null);
  const [tempAppointmentsInfoDetails, setTempAppointmentsInfoDetails] = useState(null);
  /* detail dependencies */

  /* id dependencies */
  const { selectedAppointmentsInfoId, selectedServiceIdForPackage } = useSelector(
    (state) => state.serviceProviderAppointments
  );
  /* id dependencies */

  /* module dependencies for attachment */
  const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
  const [previewImageSrc, setPreviewImageSrc] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");
  const [attachmentList, setAttachmentList] = useState([]);
  const [servicesAttachmentList, setServicesAttachmentList] = useState([]);
  const [photoProofIdList, setPhotoProofIdList] = useState([]);
  /* module dependencies for attachment */

  /* table dependencies */
  const [columns, setColumns] = useState([]);
  const { services } = useSelector((state) => state.services);
  const [searchValue] = useState("");
  const { debouncedValue: debouncedSearchValue } = useDebounce(
    searchValue,
    500
  );
  const [pagination, setPagination] = useState({
    pageNo: appConstConfig.DEFAULT_TABLE_PAGE_NUMBER,
    pageSize: appConstConfig.DEFAULT_TABLE_PAGE_SIZE,
  });
  const paginationObj = {
    total: services?.total,
    current: pagination?.pageNo,
    pageSize: pagination?.pageSize,
    onChange: (pageNo, pageSize) => setPagination({ pageNo, pageSize }),
    pageSizeOptions: appConstConfig.DEFAULT_PAGE_SIZE_OPTIONS_ARR,
    showSizeChanger: true,
  };
  /* table dependencies */

  /* dialog dependencies */
  const [openServiceInfoFormDialog, setOpenServiceInfoFormDialog] =
    useState(false);
  const [selectedServiceInfoDetails, setSelectedServiceInfoDetails] =
    useState(null);
  const [openServiceInfoDeleteDialog, setOpenServiceInfoDeleteDialog] =
    useState(false);
  /* dialog dependencies */

  /* function definition to get table data */
  const handleGetTableData = () => {
    const dataToBeSent = {
      page: debouncedSearchValue !== "" ? 1 : pagination?.pageNo,
      limit: pagination?.pageSize,
      search: debouncedSearchValue,
    };
    dispatch(getServicesAction(dataToBeSent, setLoading));
  };
  /* function definition to get table data */

  const handleGetTableDetails = () => {
    /* initial dependencies - Edit */
    if (
      selectedAppointmentsInfoId &&
      selectedAppointmentsInfoId !== "" &&
      selectedAppointmentsInfoId !== null &&
      selectedAppointmentsInfoId !== undefined
    ) {
      const dataToBeSent = {
        selectedAppointmentsInfoId,
      };
      dispatch(
        getServiceProviderAppointmentsInfoByIdAction(dataToBeSent, setLoading)
      ).then((apiRes) => {
        const response = apiRes;
        setAppointmentsInfoDetails(response);

        let tempAppointmentDetails = response;
        if (response?.order_type === "package") {
          const packageSingleService = response?.services?.find(
            (element) =>
              element?.service_id._id === selectedServiceIdForPackage
          );
          tempAppointmentDetails = packageSingleService;
        }
        setTempAppointmentsInfoDetails(tempAppointmentDetails);

        setAttachmentList(
          response?.documents?.map((element) => ({
            ...element,
            url: element?.url,
          }))
        );
        setServicesAttachmentList(
          response?.service_id?.images?.map((element) => ({
            ...element,
            url: element?.url,
          }))
        );
        setPhotoProofIdList(
          response?.documents?.map((element) => ({
            ...element,
            url: element?.url,
          }))
        );
      });
    }
  };

  /* open image preview for dialog - multiple attachment */
  const handleAttachmentPreview = async (file) => {
    const formulateAttachment = await formulateAttachmentPreview(
      file,
      file?.name,
      file.url
    );
    if (!formulateAttachment?.isFileTypeImage) {
      downloadUrl(formulateAttachment?.fileImagePreview);
    } else {
      if (!file.url && !file.preview) {
        file.preview = await readImageFileAsync(file.originFileObj);
      }
      setPreviewImageSrc(file?.url || file?.preview);
      setOpenPreviewDialog(true);
      const tempPreviewTitle =
        file?.name || file?.url?.substring(file?.url?.lastIndexOf("/") + 1);
      setPreviewTitle(tempPreviewTitle);
    }
  };
  /* open image preview for dialog - multiple attachment */

  useEffect(() => {
    handleGetTableDetails();
  }, []);

  useEffect(() => {
    handleGetTableData();
  }, [pagination, debouncedSearchValue]);

  /* initial dependencies rendering */
  useEffect(() => {
    /* table column config */
    const tempTableColumns = [
      {
        title: t("servicesList.title"),
        dataIndex: "service_name",
        key: "service_name",
      },
      {
        title: t("servicesList.price"),
        dataIndex: "service_price",
        key: "service_price",
      },
      {
        title: t("servicesList.action"),
        key: "action",
        align: "center",
        render: (_, record) => {
          return (
            <>
              <Flex gap="small" justify="center">
                <Space size="middle">
                  <Button
                    type="text"
                    size="medium"
                    icon={<EditIcon fillColor={colors.slateGray} />}
                    onClick={(e) => handleAddEditDetails(e, record)}
                  />
                </Space>
                <Space size="middle">
                  <Button
                    type="text"
                    size="medium"
                    icon={<DeleteIcon fillColor={colors.slateGray} />}
                    onClick={(e) => handleConfirmDelete(e, record)}
                  />
                </Space>
              </Flex>
            </>
          );
        },
      }
    ];
    /* table column config */
    setColumns(tempTableColumns);
  }, []);
  /* initial dependencies rendering */

  const handleAddEditDetails = (e, row) => {
    setOpenServiceInfoFormDialog(true);
    if (
      row?._id &&
      row?._id !== "" &&
      row?._id !== null &&
      row?._id !== undefined
    ) {
      const selectedServiceInfo = row;
      setSelectedServiceInfoDetails(selectedServiceInfo);
    } else {
      setSelectedServiceInfoDetails(null);
    }
  };

  const handleConfirmDelete = (e, row) => {
    setOpenServiceInfoDeleteDialog(true);
    if (
      row?._id &&
      row?._id !== "" &&
      row?._id !== null &&
      row?._id !== undefined
    ) {
      const selectedServiceInfo = row;
      setSelectedServiceInfoDetails(selectedServiceInfo);
    } else {
      setSelectedServiceInfoDetails(null);
    }
  };

  const isOrgVerified = useSelector(
    (state) => state?.auth?.loginDetails?.organization_id?.organization_verified
  );

  const openVerificationDialog = () => {
    dispatch(Actions.createAction(Actions.VERIFIED_DIALOG_OPEN));
  };

  return (
    <Flex gap="1rem" vertical>

{appointmentsInfoDetails?.user_id &&
        appointmentsInfoDetails?.user_id !== null &&
        appointmentsInfoDetails?.user_id !== undefined && (
          <GetCardLayout>
            <Flex vertical gap="2rem">
              <Flex gap="1rem">
                {(
                  <Button
                    type="default"
                    shape="circle"
                    size="small"
                    icon={<ArrowLeftOutlined />}
                    onClick={() =>
                      navigate(baseRouteMaker(routes.APPOINTMENTS_INFO))
                    }
                  />
                )}
                <Typography.Title underline level={2}>
                  {t("CommonUtils.customerInfo")}
                </Typography.Title>
              </Flex>
              {loading ? (
                <Spinner />
              ) : (
                <Row gutter={[16, 16]}>
                  <Col span={8}>
                    <RequiredLabel
                      text={t("CommonUtils.profileImage")}
                      required={false}
                    />
                    {appointmentsInfoDetails?.user_id?.profile_image ? (
                      <Image
                        style={{
                          width: "120px",
                          maxWidth: "100%",
                          border: "1px solid lightGray",
                          padding: "5px",
                          borderRadius: "10px",
                        }}
                        src={
                          appointmentsInfoDetails?.user_id?.profile_image ?? "-"
                        }
                        alt={t("CommonUtils.profileImageAlt")}
                      />
                    ) : (
                      <>-</>
                    )}
                  </Col>
                  <Col span={8}>
                    <RequiredLabel
                      text={t("CommonUtils.fullName")}
                      required={false}
                    />
                    <div>{`${
                      appointmentsInfoDetails?.user_id?.first_name ?? ""
                    } ${
                      appointmentsInfoDetails?.user_id?.last_name ?? ""
                    }`}</div>
                  </Col>
                  <Col span={8}>
                    <RequiredLabel
                      text={t("CommonUtils.email")}
                      required={false}
                    />
                    <div>{appointmentsInfoDetails?.user_id?.email ?? ""}</div>
                  </Col>
                  <Col span={8}>
                    <RequiredLabel
                      text={t("CommonUtils.phoneNumber")}
                      required={false}
                    />
                    <div>{`+${
                      appointmentsInfoDetails?.user_id?.phone?.country_code ??
                      ""
                    } ${
                      appointmentsInfoDetails?.user_id?.phone?.phone_number ??
                      ""
                    }`}</div>
                  </Col>
                  <Col span={8}>
                    <RequiredLabel text={t("ProfileSetup.countryLabel")} required={false} />
                    <div>{appointmentsInfoDetails?.user_id?.country?.name || "-"}</div>
                  </Col>
                  <Col span={8}>
                    <RequiredLabel
                      text={t("CommonUtils.city")}
                      required={false}
                    />
                    <div>
                      {appointmentsInfoDetails?.user_id?.address?.city?.name ||
                        "-"}
                    </div>
                  </Col>
                  <Col span={8}>
                    <RequiredLabel
                      text={t("CommonUtils.state")}
                      required={false}
                    />
                    <div>
                      {appointmentsInfoDetails?.user_id?.address?.state?.name ||
                        "-"}
                    </div>
                  </Col>
                  <Col span={8}>
                    <RequiredLabel
                      text={t("CommonUtils.zipCode")}
                      required={false}
                    />
                    <div>
                      {appointmentsInfoDetails?.user_id?.address?.zip_code ||
                        "-"}
                    </div>
                  </Col>
                  <Col span={8}>
                    <RequiredLabel
                      text={t("CommonUtils.gender")}
                      required={false}
                    />
                    <div>{appointmentsInfoDetails?.user_id?.gender || "-"}</div>
                  </Col>
                  <Col span={8}>
                    <RequiredLabel
                      text={t("CommonUtils.dateOfBirth")}
                      required={false}
                    />
                    <div>
                      {appointmentsInfoDetails?.user_id?.date_of_birth ? dateObjToDispViewDate(
                        appointmentsInfoDetails?.user_id?.date_of_birth,
                        "ddmmyyyy"
                      ) ?? "-" : "-"}
                    </div>
                  </Col>
                  <Col span={8}>
                    <RequiredLabel
                      text={t("CommonUtils.weight")}
                      required={false}
                    />
                    <div>{appointmentsInfoDetails?.user_id?.weight ? `${
                      appointmentsInfoDetails?.user_id?.weight?.number ?? "-"
                    } ${
                      appointmentsInfoDetails?.user_id?.weight?.unit ?? "-"
                    }` : "-"}</div>
                  </Col>
                  <Col span={8}>
                    <RequiredLabel
                      text={t("CommonUtils.height")}
                      required={false}
                    />
                    <div>
                      {appointmentsInfoDetails?.user_id?.height
                        ? `${
                            appointmentsInfoDetails?.user_id?.height?.number ??
                            "-"
                          } ${
                            appointmentsInfoDetails?.user_id?.height?.unit ??
                            "-"
                          }`
                        : "-"}
                    </div>
                  </Col>
                  <Col span={8}>
                    <RequiredLabel
                      text={t("CommonUtils.accountStatus")}
                      required={false}
                    />
                    <StatusTag status={appointmentsInfoDetails?.user_id?.status === "active" ? true : false}/>
                  </Col>
                  <Col span={24}>
                    <RequiredLabel
                      text={t("CommonUtils.photoIDProof")}
                      required={false}
                    />
                    {photoProofIdList?.length !== 0 &&
                    photoProofIdList[0]?.url !== "" ? (
                      <Flex vertical align="center" justify="center">
                        <Upload
                          listType="picture-card"
                          fileList={photoProofIdList}
                          accept="image/*"
                          onPreview={handleAttachmentPreview}
                          className="uploadContainer"
                        ></Upload>
                        <Modal
                          open={openPreviewDialog}
                          title={previewTitle}
                          footer={null}
                          onCancel={() => setOpenPreviewDialog(false)}
                        >
                          <img
                            style={{ width: "100%" }}
                            src={previewImageSrc}
                            alt={t("CommonUtils.photoProofAlt")}
                          />
                        </Modal>
                      </Flex>
                    ) : (
                      <>-</>
                    )}
                  </Col>
                </Row>
              )}
            </Flex>
          </GetCardLayout>
        )}
        
      {/* Appointment Info */}
      {(
          <GetCardLayout>
              <Flex vertical gap="2rem">
                <Title underline level={2}>
                  {t("CommonUtils.appointmentInfo")}
                </Title>
              <Row gutter={[16, 16]}>
                {
                  appointmentsInfoDetails?.order_type === "package" &&
              <Col span={8}>
                  <RequiredLabel
                    text={t("CommonUtils.service")}
                    required={false}
                  />
                  <div>
                    {tempAppointmentsInfoDetails?.service_name
                      ? tempAppointmentsInfoDetails?.service_name
                      : "-"}
                  </div>
                </Col>
                }
              <Col span={8}>
                  <RequiredLabel
                    text={t("Modules.Appointments.appointmentTime")}
                    required={false}
                  />
                  <div>
                    {timeObjToDispViewTimeWithSuffix(dayjs(tempAppointmentsInfoDetails?.appointment_date_time).utc().local().format('ddd, MMM D, H:mm z'))}
                  </div>
                </Col>
                <Col span={8}>
                  <RequiredLabel
                    text={t("Modules.Appointments.appointmentDate")}
                    required={false}
                  />
                  <div>
                    {dateObjToDispViewDate(
                      tempAppointmentsInfoDetails?.appointment_date,
                      "ddmmyyyy"
                    )}
                  </div>
                </Col>
                <Col span={8}>
                  <RequiredLabel
                    text={t("Modules.Appointments.bookingFor")}
                    required={false}
                  />
                  <div>{tempAppointmentsInfoDetails?.booking_for}</div>
                </Col>
                <Col span={8}>
                  <RequiredLabel
                    text={t("Modules.Appointments.charges")}
                    required={false}
                  />
                  <div>{appointmentsInfoDetails?.total_amount?.toFixed(2)}</div>
                </Col>

                <Col span={8}>
                  <RequiredLabel
                    text={t("Modules.Appointments.paymentStatus")}
                    required={false}
                  />
                  <Tag
                    bordered={false}
                    style={{
                      background: appointmentsInfoDetails?.is_payment_completed
                        ? colors.lightTeal
                        : colors.lightPinkishRed,
                      color: appointmentsInfoDetails?.is_payment_completed
                        ? colors.darkTeal
                        : colors.darkMaroon,
                    }}
                  >
                    {appointmentsInfoDetails?.is_payment_completed
                      ? t("Modules.Appointments.paid")
                      : t("Modules.Appointments.due")}
                  </Tag>
                </Col>
                <Col span={8}>
                  <RequiredLabel
                    text={t("Modules.Appointments.orderNumber")}
                    required={false}
                  />
                  <div>{appointmentsInfoDetails?.order_number}</div>
                </Col>

                {appointmentsInfoDetails?.booking_for === "other" && (
                  <Col span={24}>
                    <Flex vertical gap="1rem">
                      <Typography.Title level={3}>
                        {t("Modules.Appointments.bookingForDetails")}
                      </Typography.Title>
                      <Row gutter={[16, 16]}>
                        <Col span={8}>
                          <RequiredLabel
                            text={t("Modules.Appointments.name")}
                            required={false}
                          />
                          <div>{`${appointmentsInfoDetails?.booking_for_details?.first_name} ${appointmentsInfoDetails?.booking_for_details?.last_name}`}</div>
                        </Col>
                        <Col span={8}>
                          <RequiredLabel
                            text={t("Modules.Appointments.email")}
                            required={false}
                          />
                          <div>
                            {
                              appointmentsInfoDetails?.booking_for_details
                                ?.email
                            }
                          </div>
                        </Col>
                        <Col span={8}>
                          <RequiredLabel
                            text={t("Modules.Appointments.relationship")}
                            required={false}
                          />
                          <div>
                            {
                              appointmentsInfoDetails?.booking_for_details
                                ?.relationship
                            }
                          </div>
                        </Col>
                      </Row>
                    </Flex>
                  </Col>
                )}
              </Row>
            </Flex>
          </GetCardLayout>
        )}

      {/* Service Info */}
      {appointmentsInfoDetails?.service_id &&
        appointmentsInfoDetails?.service_id !== null &&
        appointmentsInfoDetails?.service_id !== undefined && (
          <GetCardLayout>
              <Flex vertical gap="2rem">
                <Title underline level={2}>
                  {t("CommonUtils.serviceInfo")}
                </Title>
              <Row gutter={[16, 16]}>
                <Col span={24}>
                  <RequiredLabel
                    text={t("CommonUtils.documents")}
                    required={false}
                  />
                  {servicesAttachmentList?.length !== 0 ? (
                    <Flex vertical align="center" justify="center">
                      <Upload
                        listType="picture-card"
                        fileList={servicesAttachmentList}
                        accept="image/*"
                        onPreview={handleAttachmentPreview}
                        className="uploadContainer"
                      ></Upload>
                      <Modal
                        open={openPreviewDialog}
                        title={previewTitle}
                        footer={null}
                        onCancel={() => setOpenPreviewDialog(false)}
                      >
                        <img style={{ width: "100%" }} src={previewImageSrc} />
                      </Modal>
                    </Flex>
                  ) : (
                    <>-</>
                  )}
                </Col>

                <Col span={8}>
                  <RequiredLabel
                    text={t("CommonUtils.service")}
                    required={false}
                  />
                  <div>
                    {appointmentsInfoDetails?.service_id
                      ? appointmentsInfoDetails?.service_id?.title
                      : "-"}
                  </div>
                </Col>
                <Col span={8}>
                  <RequiredLabel
                    text={t("CommonUtils.category")}
                    required={false}
                  />
                  <div>
                    {appointmentsInfoDetails?.service_id
                      ? appointmentsInfoDetails?.service_id?.categoryInfo
                          ?.category?.name
                      : "-"}
                  </div>
                </Col>
                <Col span={8}>
                  <RequiredLabel
                    text={t("CommonUtils.subCategory")}
                    required={false}
                  />
                  <div>
                    {appointmentsInfoDetails?.service_id
                      ? appointmentsInfoDetails?.service_id?.categoryInfo
                          ?.subcategory?.name
                      : "-"}
                  </div>
                </Col>
                <Col span={8}>
                  <RequiredLabel
                    text={t("CommonUtils.availability")}
                    required={false}
                  />
                  <div>
                    <ol>
                      {appointmentsInfoDetails?.service_id &&
                        appointmentsInfoDetails?.service_id?.availability?.map(
                          (element) => {
                            return (
                              <li>{`${element.day} - ${element.startTime} to ${element.endTime}`}</li>
                            );
                          }
                        )}
                    </ol>
                  </div>
                </Col>
              </Row>
            </Flex>
          </GetCardLayout>
        )}


      {/* Staff Info */}
      {userSession?.userRole === userRoles.SERVICE_PROVIDER_ADMIN &&
        appointmentsInfoDetails?.service_provider_id &&
        appointmentsInfoDetails?.service_provider_id !== null &&
        appointmentsInfoDetails?.service_provider_id !== undefined && (
          <GetCardLayout>
            <Flex vertical gap="2rem">
                <Title underline level={2}>
                  {t("CommonUtils.staffInfo")}
                </Title>
              <Row gutter={[16, 16]}>
                <Col span={8}>
                  <RequiredLabel
                    text={t("CommonUtils.profileImage")}
                    required={false}
                  />
                  {appointmentsInfoDetails?.service_provider_id
                    ?.profile_image ? (
                    <Image
                      style={{
                        width: "120px",
                        maxWidth: "100%",
                        border: "1px solid lightGray",
                        padding: "5px",
                        borderRadius: "10px",
                      }}
                      src={
                        appointmentsInfoDetails?.service_provider_id
                          ?.profile_image ?? "-"
                      }
                      alt={t("CommonUtils.profileImageAlt")}
                    />
                  ) : (
                    <>-</>
                  )}
                </Col>

                <Col span={8}>
                  <RequiredLabel
                    text={t("CommonUtils.staffName")}
                    required={false}
                  />
                  <div>
                    {appointmentsInfoDetails?.service_provider_id
                      ? `${appointmentsInfoDetails?.service_provider_id?.first_name} ${appointmentsInfoDetails?.service_provider_id?.last_name}`
                      : "-"}
                  </div>
                </Col>
                <Col span={8}>
                  <RequiredLabel
                    text={t("CommonUtils.email")}
                    required={false}
                  />
                  <div>
                    {appointmentsInfoDetails?.service_provider_id
                      ? appointmentsInfoDetails?.service_provider_id?.email
                      : "-"}
                  </div>
                </Col>
                <Col span={8}>
                  <RequiredLabel
                    text={t("CommonUtils.phoneNumber")}
                    required={false}
                  />
                  <div>
                    {appointmentsInfoDetails?.service_provider_id
                      ? `+${appointmentsInfoDetails?.service_provider_id?.phone?.country_code}-${appointmentsInfoDetails?.service_provider_id?.phone?.phone_number}`
                      : "-"}
                  </div>
                </Col>
                <Col span={8}>
                  <RequiredLabel
                    text={t("CommonUtils.fees")}
                    required={false}
                  />
                  <div>
                    {appointmentsInfoDetails?.service_provider_id
                      ? appointmentsInfoDetails?.service_provider_id?.fees
                      : "-"}
                  </div>
                </Col>
                <Col span={8}>
                  <RequiredLabel
                    text={t("CommonUtils.experience")}
                    required={false}
                  />
                  <div>
                    {appointmentsInfoDetails?.service_provider_id
                      ? `${
                          appointmentsInfoDetails?.service_provider_id
                            ?.experience
                        } ${t("CommonUtils.experienceUnit")}`
                      : "-"}
                  </div>
                </Col>
                <Col span={8}>
                  <RequiredLabel
                    text={t("CommonUtils.availability")}
                    required={false}
                  />
                  <div>
                    <ol>
                      {appointmentsInfoDetails?.service_provider_id &&
                        appointmentsInfoDetails?.service_provider_id?.availability?.map(
                          (element) => {
                            return (
                              <li>{`${element.day} - ${element.startTime} to ${element.endTime}`}</li>
                            );
                          }
                        )}
                    </ol>
                  </div>
                </Col>
              </Row>
            </Flex>
          </GetCardLayout>
        )}

      {userSession?.userRole === userRoles.SERVICE_PROVIDER_ADMIN &&
        !["REJECTED", "COMPLETED"].includes(
          appointmentsInfoDetails?.system_status
        ) && (
          <>
            <GetCardLayout>
              <Flex vertical gap="2rem">
                <Flex align="center" justify="space-between">
                  <Title level={2}>{t("servicesList.services")}</Title>
                  <Button
                    type="primary"
                    onClick={(e) =>
                      isOrgVerified
                        ? handleAddEditDetails(e, null)
                        : openVerificationDialog()
                    }
                  >
                    {t("servicesList.addService")}
                  </Button>
                </Flex>
                {loading ? (
                  <Spinner />
                ) : (
                  <Table
                    dataSource={appointmentsInfoDetails?.extra_services}
                    columns={columns}
                    pagination={paginationObj}
                  />
                )}
              </Flex>
            </GetCardLayout>
            <ServiceInfoFormDialog
              open={openServiceInfoFormDialog}
              onClose={() => {
                setOpenServiceInfoFormDialog(false);
                handleGetTableDetails();
                setSelectedServiceInfoDetails(null);
              }}
              selectedServiceInfoDetails={selectedServiceInfoDetails}
            />
            <ServiceInfoDeleteDialog
              open={openServiceInfoDeleteDialog}
              onClose={() => {
                setOpenServiceInfoDeleteDialog(false);
                handleGetTableDetails();
                setSelectedServiceInfoDetails(null);
              }}
              selectedServiceInfoDetails={selectedServiceInfoDetails}
            />
          </>
        )}
    </Flex>
  );
};

export default ServiceInfoList;
